// Through the package, not relatively, so the `browser` export condition
// can swap in a stand-in where there's no async_hooks.
// eslint-disable-next-line local-rules/no-self-package-import, import/no-extraneous-dependencies
import { AsyncLocalStorage } from '@frameio/async-hooks/async-storage';

export type TaskVar<T> = {
  get isAsync(): boolean;
  enterGlobal(value: T): void;
  disableGlobal(): void;
  getStore(): T | undefined;
  run<R, TArgs extends any[]>(
    store: T,
    callback: (...args: TArgs) => R,
    ...args: TArgs
  ): R;
  exit<R, TArgs extends any[]>(
    callback: (...args: TArgs) => R,
    ...args: TArgs
  ): R;
  enterWith(store: T): void;
};

export class GlobalVar<T> implements TaskVar<T> {
  get isAsync() {
    return false;
  }

  enterGlobal(value?: T) {
    this.#current = value;
  }

  getStore(): T | undefined {
    return this.#current;
  }

  run<R, TArgs extends any[]>(
    store: T,
    callback: (...args: TArgs) => R,
    ...args: TArgs
  ): R {
    const previous = this.#current;
    this.#current = store;
    try {
      return callback(...args);
    } finally {
      this.#current = previous;
    }
  }

  exit<R, TArgs extends any[]>(
    callback: (...args: TArgs) => R,
    ...args: TArgs
  ): R {
    const prev = this.#current;
    this.#current = undefined;
    try {
      return callback(...args);
    } finally {
      this.#current = prev;
    }
  }

  enterWith(__: T): void {
    throw new Error('enterWith requires suport for async_hooks, try run()');
  }

  disable(): void {
    throw new Error('disable requires suport for async_hooks, try exit()');
  }

  disableGlobal(): void {
    this.#current = undefined;
  }

  #current: T | undefined;
}

export const TaskVar = AsyncLocalStorage
  ? class AsyncTaskVar<T> extends AsyncLocalStorage<T> implements TaskVar<T> {
      get isAsync() {
        return true;
      }

      enterGlobal(value: T) {
        this.enterWith(value);
      }

      disableGlobal() {
        this.disable();
      }
    }
  : GlobalVar;
